import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import type { EscritorioMembro } from '@/api/client';
import type { Responsavel } from '@/api/distribuicao';
import type { Equipe } from '@/api/equipes';
import {
  alterarRegra,
  criarRegra,
  previaDaRegra,
  type CampoRegra,
  type CondicaoRegra,
  type ModoRegra,
  type PreviaDaRegra,
  type RegraDistribuicao,
} from '@/api/regrasDistribuicao';
import { Opcao } from '@/components/escritorio/DestinosPicker';
import { descreverResponsaveis } from '@/components/escritorio/ResponsaveisDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { useDebounce } from '@/hooks/useDebounce';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { CondicaoEditor, criterioIaValido, faixaInvertida, valorValido } from './CondicaoEditor';
import { CAMPOS, sugerirNome } from './rotulos';

interface RegraDialogProps {
  escritorioId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ausente: regra nova. */
  regra?: RegraDistribuicao | null;
  membros: EscritorioMembro[];
  equipes: Equipe[];
}

type CriterioNaTela = CondicaoRegra & { chave: number };

let proximaChave = 1;
const naTela = (c: CondicaoRegra): CriterioNaTela => ({ ...c, chave: proximaChave++ });

function completo(c: CondicaoRegra): boolean {
  if (c.campo === 'IA') return criterioIaValido(c);
  if (c.campo !== 'VALOR') return c.valores.length > 0;
  const ok = (v?: string | null) => !v || valorValido(v);
  return !!(c.minimo || c.maximo) && ok(c.minimo) && ok(c.maximo) && !faixaInvertida(c);
}

/** A regra lida como frase: quando o processo tiver (E entre critérios, OU dentro de cada um), então vai para. */
export function RegraDialog(props: RegraDialogProps) {
  // Remonta a cada abertura: o formulário começa da regra (ou vazio), nunca do que ficou da última.
  return props.open ? <RegraForm key={props.regra?.id ?? 'nova'} {...props} /> : null;
}

function RegraForm({ escritorioId, open, onOpenChange, regra, membros, equipes }: RegraDialogProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  // Vazio: vale o nome sugerido pelos critérios e pelo destino.
  const [nome, setNome] = useState(regra?.nome ?? '');
  const [modo, setModo] = useState<ModoRegra>(regra?.modo ?? 'SUGERIR');
  const [criterios, setCriterios] = useState<CriterioNaTela[]>(() => (regra?.condicoes ?? []).map(naTela));
  const [destino, setDestino] = useState<Responsavel | null>(regra?.destino ?? null);
  // O critério que acabou de entrar recebe o cursor.
  const [recemChegado, setRecemChegado] = useState<number | null>(null);
  // Depois da primeira tentativa de salvar, o que falta aparece junto de cada campo.
  const [tentou, setTentou] = useState(false);

  const usados = new Set(criterios.map((c) => c.campo));
  // Decisão do Adriano (27/10): com o critério da IA, a regra só sugere.
  const comIa = usados.has('IA');
  const modoFinal: ModoRegra = comIa ? 'SUGERIR' : modo;
  const disponiveis = CAMPOS.filter((c) => !usados.has(c.campo));
  const prontos = criterios.filter(completo).map(({ chave: _chave, ...c }) => c);
  // O critério da IA fica fora da prévia: texto novo nunca tem resposta, e a conta só gastaria
  // servidor. A prévia mostra o que o resto da regra pega; a IA escolhe entre esses depois.
  const daPrevia = prontos.filter((c) => c.campo !== 'IA');
  const iaPronta = prontos.some((c) => c.campo === 'IA');
  const iaOriginal = regra?.condicoes.find((c) => c.campo === 'IA')?.valores[0];
  // A regra ligada cujo destino saiu do escritório precisa de outro: o servidor recusa o velho.
  const destinoVelhoInvalido =
    !!regra?.destino_invalido &&
    regra.ativa &&
    destino?.tipo === regra.destino.tipo &&
    destino?.valor === regra.destino.valor;
  const faltaDestino = !destino || destinoVelhoInvalido;
  const nomeDoDestino = destino && !faltaDestino ? descreverResponsaveis([destino], equipes, membros) : '';
  const sugerido = sugerirNome(prontos, nomeDoDestino);
  const nomeFinal = nome.trim() || sugerido;

  const faltaCriterio = criterios.length === 0;
  const valida = !faltaCriterio && prontos.length === criterios.length && !faltaDestino && !!nomeFinal;

  const acrescentar = (campo: CampoRegra) => {
    const novo = naTela({ campo, valores: [] });
    setCriterios([...criterios, novo]);
    setRecemChegado(novo.chave);
  };

  // A prévia acompanha os critérios sozinha, quando o gestor para de mexer.
  const chaveDaPrevia = useDebounce(JSON.stringify(daPrevia), 600);
  const previa = useQuery({
    queryKey: ['regras-distribuicao', 'previa', escritorioId, chaveDaPrevia],
    queryFn: () => previaDaRegra(escritorioId, JSON.parse(chaveDaPrevia) as CondicaoRegra[]),
    enabled: chaveDaPrevia !== '[]',
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });

  const salvar = useMutation({
    mutationFn: () => {
      const corpo = {
        nome: nomeFinal,
        // A regra nova nasce ligada; ligar e desligar fica na lista.
        ativa: regra?.ativa ?? true,
        modo: modoFinal,
        condicoes: prontos,
        destino: destino as Responsavel,
      };
      return regra
        ? alterarRegra(escritorioId, regra.id, { ...corpo, atualizada_em: regra.updated_at })
        : criarRegra(escritorioId, corpo);
    },
    onSuccess: () => {
      showSuccess(regra ? 'Regra salva' : 'Regra criada');
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.regras(escritorioId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.fila(escritorioId) });
      onOpenChange(false);
    },
    onError: (err) => {
      notifyMutationError(err, showError);
      // 409: outra tela salvou. A lista volta a valer do servidor e o diálogo fecha, para a
      // próxima tentativa partir da versão nova (senão repetiria o 409).
      if (isAxiosError(err) && err.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.regras(escritorioId) });
        onOpenChange(false);
      }
    },
  });

  const tentarSalvar = () => {
    if (valida) {
      salvar.mutate();
      return;
    }
    setTentou(true);
    // O recém-chegado também conta: agora ele já pode ficar vermelho.
    setRecemChegado(null);
    // Leva o gestor ao primeiro que falta, depois que os avisos aparecerem.
    requestAnimationFrame(() => {
      document.querySelector('[data-erro-regra]')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
  };

  const ehDestino = (r: Responsavel) => destino?.tipo === r.tipo && destino?.valor === r.valor;
  const aviso = (texto: string) => (
    <p role="alert" className="text-xs text-destructive" data-erro-regra>
      {texto}
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Cabeçalho e rodapé fixos, só o meio rola; no celular, a tela inteira. */}
      <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:p-0 sm:max-w-2xl max-sm:h-dvh max-sm:max-h-dvh max-sm:w-full max-sm:rounded-none max-sm:border-0">
        <DialogHeader className="border-b px-4 pb-3 pt-4 pr-12 sm:px-6 sm:pt-6">
          <DialogTitle>{regra ? 'Editar regra' : 'Nova regra de distribuição'}</DialogTitle>
          <DialogDescription>Os critérios valem juntos; dentro de cada um, basta um dos valores.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4 sm:px-6">
          <section className="space-y-2" aria-labelledby="regra-quando">
            <h3 id="regra-quando" className="text-sm font-semibold">
              Quando o processo tiver
            </h3>
            {criterios.map((c, i) => (
              <div key={c.chave} className="space-y-2">
                {i > 0 && (
                  <p className="pl-3 text-xs font-semibold uppercase text-muted-foreground" aria-hidden="true">
                    e
                  </p>
                )}
                <CondicaoEditor
                  id={`regra-criterio-${c.chave}`}
                  cond={c}
                  membros={membros}
                  focar={c.chave === recemChegado}
                  incompleto={tentou && !completo(c) && c.chave !== recemChegado}
                  iaOriginal={c.campo === 'IA' ? iaOriginal : undefined}
                  onChange={(novo) => setCriterios(criterios.map((x, j) => (j === i ? { ...novo, chave: x.chave } : x)))}
                  onRemover={() => setCriterios(criterios.filter((_, j) => j !== i))}
                />
              </div>
            ))}
            {disponiveis.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" className="h-11 sm:h-9">
                    <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
                    Critério
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="max-h-[50dvh] w-72 overflow-y-auto"
                  // O cursor vai para o critério novo, não de volta ao botão.
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  {disponiveis.map((c) => (
                    <DropdownMenuItem
                      key={c.campo}
                      className="flex-col items-start gap-0 py-2"
                      onSelect={() => acrescentar(c.campo)}
                    >
                      <span>{c.rotulo}</span>
                      <span className="text-xs text-muted-foreground">{c.ajuda}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {tentou && faltaCriterio && aviso('Acrescente ao menos um critério.')}
          </section>

          <section className="space-y-2" aria-labelledby="regra-destino">
            <h3 id="regra-destino" className="text-sm font-semibold">
              Então vai para
            </h3>
            {regra?.destino_invalido && (
              <Alert>
                <AlertDescription>
                  Quem recebia por esta regra saiu do escritório (ou a equipe ficou vazia). Escolha outro destino.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap gap-2">
              {equipes
                .filter((e) => e.membros.length > 0)
                .map((e) => {
                  const alvo: Responsavel = { tipo: 'EQUIPE', valor: e.id };
                  return (
                    <Opcao key={e.id} ativa={ehDestino(alvo)} onClick={() => setDestino(alvo)}>
                      Equipe {e.nome}
                    </Opcao>
                  );
                })}
              {membros.map((m) => {
                const alvo: Responsavel = { tipo: 'USUARIO', valor: m.usuario_id };
                return (
                  <Opcao key={m.usuario_id} ativa={ehDestino(alvo)} onClick={() => setDestino(alvo)}>
                    {m.usuario_nome}
                  </Opcao>
                );
              })}
            </div>
            {tentou && faltaDestino && aviso(destinoVelhoInvalido ? 'Escolha outro destino.' : 'Escolha para quem vai.')}
            <RadioGroup
              value={modoFinal}
              onValueChange={(v) => setModo(v as ModoRegra)}
              className="space-y-2 pt-2"
              aria-label="O que a regra faz"
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem id="regra-sugerir" value="SUGERIR" className="mt-0.5" />
                <Label htmlFor="regra-sugerir" className="font-normal leading-snug">
                  Só sugerir — o gestor confirma na aba Distribuir
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem
                  id="regra-aplicar"
                  value="APLICAR"
                  className="mt-0.5"
                  disabled={comIa}
                  aria-describedby={comIa ? 'regra-so-sugere' : undefined}
                />
                <Label htmlFor="regra-aplicar" className={`font-normal leading-snug ${comIa ? 'opacity-60' : ''}`}>
                  Aplicar automaticamente — distribui sozinha o que chegar daqui em diante, quando tiver certeza, e avisa
                  quem recebe. O que já está na fila continua como sugestão.
                </Label>
              </div>
            </RadioGroup>
            {comIa && (
              <p id="regra-so-sugere" className="text-xs text-muted-foreground">
                Com o critério da IA, a regra só sugere.
              </p>
            )}
          </section>

          <div className="space-y-1.5">
            <Label htmlFor="regra-nome">Nome</Label>
            <Input
              id="regra-nome"
              value={nome}
              maxLength={120}
              placeholder={sugerido || 'Sai dos critérios e do destino'}
              onChange={(e) => setNome(e.target.value)}
            />
            {!nome.trim() && sugerido && (
              <p className="text-xs text-muted-foreground">Sem um nome seu, a regra fica com o sugerido.</p>
            )}
          </div>
        </div>

        <div className="space-y-2 border-t px-4 py-3 sm:px-6">
          <TextoDaPrevia
            vazia={chaveDaPrevia === '[]'}
            previa={previa.data}
            calculando={previa.isFetching || chaveDaPrevia !== JSON.stringify(daPrevia)}
            comIa={iaPronta}
            erro={previa.isError ? classifyError(previa.error).message : null}
          />
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" className="h-11 sm:h-9" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" className="h-11 sm:h-9" disabled={salvar.isPending} onClick={tentarSalvar}>
              {salvar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
              {regra ? 'Salvar' : 'Criar regra'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TextoDaPrevia({
  vazia,
  previa,
  calculando,
  erro,
  comIa = false,
}: {
  vazia: boolean;
  /** A regra tem critério da IA: a conta só inclui o que ela já avaliou. */
  comIa?: boolean;
  previa?: PreviaDaRegra;
  calculando: boolean;
  /** Por que o servidor recusou (um critério que ele não aceita diz qual). */
  erro: string | null;
}) {
  const n = (x: number) => x.toLocaleString('pt-BR');
  let texto: ReactNode;
  if (vazia && comIa) {
    texto = 'A IA avalia os processos da fila em segundo plano depois de salvar.';
  } else if (vazia) {
    texto = 'Com um critério completo, aqui aparece com quantos processos a regra casaria.';
  } else if (erro !== null && !calculando) {
    texto = <span className="text-destructive">Não deu para calcular a prévia: {erro}</span>;
  } else if (!previa) {
    texto = 'Calculando com quantos processos a regra casaria…';
  } else if (comIa) {
    texto = (
      <>
        Sem o critério da IA, casaria com <strong>{n(previa.fila_casa)}</strong> na fila
        {previa.fila_aguardando > 0 && ` (+${n(previa.fila_aguardando)} aguardando dados)`}. A IA escolhe entre esses
        depois de salvar, em segundo plano.
      </>
    );
  } else {
    texto = (
      <>
        Casaria com <strong>{n(previa.fila_casa)}</strong> na fila
        {previa.fila_aguardando > 0 && ` (+${n(previa.fila_aguardando)} aguardando dados)`} e{' '}
        <strong>{n(previa.acervo_casa)}</strong> no acervo
        {previa.acervo_aguardando > 0 && ` (+${n(previa.acervo_aguardando)} sem os dados)`}
        {previa.acervo_parcial && ', entre os 20 mil mais recentes'}.
      </>
    );
  }
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
      {calculando && !vazia && <Spinner size={16} />}
      <span className={calculando && previa ? 'opacity-60' : undefined}>{texto}</span>
    </p>
  );
}
